import { autoJsStoreUtils, autoWebViewJs, isRealInAndroid } from "@droid/android";
import { Log, useTranslation } from "@fanfanlo";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { createDefServerStatus, IServerStatus } from "./intf";
import { ScaneQRCodeButton } from "./ScanQRCodeButton";

const fileLog = new Log(false, "SettingPanel_file")
fileLog.childrenPaused = true;
fileLog.pause = true;

interface ISettingPanelProps {
    open: boolean;
    onClose: () => void;
}

export function SettingPanel(props: ISettingPanelProps) {
    const uiLog = fileLog.sub(false, "  SettingPanel_ui");
    uiLog.childrenPaused = true;
    uiLog.pause = true;
    const { t } = useTranslation('homepage/components/notification-listener/content');
    const { open, onClose } = props;

    const [initialSetting, setInitialSetting] = useState<IServerStatus>(createDefServerStatus());
    
    const [portInput, setPortInput] = useState('6001'); // 设置默认值
    const [portError, setPortError] = useState('');

    function checkUpdateSetting(){
        uiLog.log('checkUpdateSetting_fn');
        if (portError) {
            uiLog.warn('Port has an error, skipping update.');
            return;
        }

        const currentSetting: IServerStatus = createDefServerStatus();

        const settingBase64 = autoJsStoreUtils.btoaUtf8(JSON.stringify(currentSetting));
        const script = `
        com.fanfanlo.lib.notification.NotificationSocketServer.updateSettingBase64('${settingBase64}');
        `;
        uiLog.log('Calling updateSettingBase64 with script:', script);
        const { errMsg } = autoWebViewJs.callScript(script);
        if (errMsg) {
            uiLog.error('updateSettingBase64 error:', errMsg);
        }
    }

    useEffect(() => {
        uiLog.log('useEffect_fn, open changed:', open);
        if (open) {
            const script = `
            var s = com.fanfanlo.lib.notification.NotificationSocketServer.getStatus();
            s;`
            const { javaResultString, errMsg } = autoWebViewJs.callScript<string>(script);
            if (errMsg) {
                uiLog.error('getStatus error:', errMsg);
                return;
            }
            let str = javaResultString || ''// autoJsStoreUtils.atobUtf8(javaResultString || '');
            if(!str){
                if(isRealInAndroid){
                    uiLog.error('atobUtf8 result empty, javaResultString=', javaResultString);
                    return;
                }else{
                    str = ``
                }
            }
            const storedSetting = JSON.parse(str || '{}') as IServerStatus;
            
            setInitialSetting(storedSetting);
            // 确保 port 有值，否则使用默认值 6001
            const port = storedSetting.port || 6001;
            setPortInput(String(port));
            setPortError('');
        }
    }, [open]);

    const handlePortChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        // 确保值不为空
        if (value === '') {
            setPortInput('');
            setPortError(t('SettingPanel.portRangeError'));
            return;
        }
        
        setPortInput(value);

        const portNum = Number(value);
        if (!Number.isInteger(portNum) || portNum <= 1024 || portNum > 65535) {
            setPortError(t('SettingPanel.portRangeError'));
        } else {
            setPortError('');
        }
    };


    // “使用”按钮的点击事件处理器
    const handleApply = () => {
        uiLog.log('handleApply_fn');
        checkUpdateSetting(); // 保存设置
        onClose(); // 关闭对话框
    };

    const handleInputFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setTimeout(() => {
            event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300);
    };

    return (
        // Dialog 的 onClose 直接使用父组件的 onClose，不执行任何保存操作
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{t('SettingPanel.modalTitle')}</DialogTitle>
            <DialogContent>
                <ScaneQRCodeButton />
            </DialogContent>
            <DialogContent>
                <TextField
                    margin="dense"
                    id="port"
                    label={t('SettingPanel.portLabel')}
                    type="number"
                    fullWidth
                    variant="standard"
                    value={portInput}
                    onChange={handlePortChange}
                    onFocus={handleInputFocus}
                    error={!!portError}
                    helperText={portError}
                    inputProps={{
                        min: 1025,
                        max: 65535,
                    }}
                    data-testid="SettingPanel__portInput__01JG2B0093"
                    data-test-comment="端口号输入框，输入范围1025-65535，用于配置Socket服务器端口"
                />
                <DialogContentText sx={{ mt: 2 }}>
                    {t('SettingPanel.description')}
                </DialogContentText>
                <DialogContentText color="error" sx={{ mt: 2 }}>
                    {t('SettingPanel.riskWarning')}
                </DialogContentText>
            </DialogContent>
            {/* 恢复 DialogActions 和 Buttons */}
            <DialogActions>
                {/* "放弃"按钮直接调用 onClose，不保存 */}
                <Button onClick={onClose} data-testid="SettingPanel__cancelButton__01JG2B0091" data-test-comment="取消按钮，点击后关闭设置面板，不保存任何更改">{t('SettingPanel.cancelButton')}</Button>
                {/* "使用"按钮调用 handleApply 来保存 */}
                <Button onClick={handleApply} disabled={!!portError} data-testid="SettingPanel__applyButton__01JG2B0092" data-test-comment="应用按钮，点击后保存设置并关闭面板，端口错误时禁用">{t('SettingPanel.applyButton')}</Button>
            </DialogActions>
        </Dialog>
    );
}
